/**
 * QuoteTool — interactive Build-Your-Pack quote on the /pricing page.
 *
 * Replaces the previous static Free/Plus/Premium membership card grid.
 * Two-step funnel:
 *   Step 1: Dog count (1, 2, 3, 4+)
 *   Step 2: Frequency (Weekly / Every other week / Just once)
 *
 * As soon as a frequency is picked, POST /api/quote fires and the
 * result panel renders the live per-visit total. The total comes
 * straight from the engine — no client-side math, no hardcoded numbers.
 *
 * "4+" is sent as `dog_count=4` (since pricing is LINEAR — see
 * /api/quote handback note. A 5-dog pack is just 5 × per_dog. Users
 * who pick 4+ get the 4-dog floor quote with a "starts at" label so
 * they don't think it's the literal answer for 7 dogs).
 *
 * Brand caps preserved: PooPluckers / PooPartner / Plucking.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RotateCcw, ChevronLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const DOG_OPTIONS = [
  { value: 1, label: "1 dog" },
  { value: 2, label: "2 dogs" },
  { value: 3, label: "3 dogs" },
  { value: 4, label: "4+ dogs", capped: true },
];

const FREQUENCY_OPTIONS = [
  {
    value: "weekly",
    label: "Weekly",
    sub: "Consistently clean yard every week.",
  },
  {
    value: "every_other_week",
    label: "Every other week",
    sub: "Steady cadence for lighter-traffic yards.",
  },
  {
    value: "just_once",
    label: "Just once",
    sub: "One-time Plucking. No commitment.",
  },
];

const fmtMoney = (n) =>
  n === null || n === undefined
    ? ""
    : n % 1 === 0
      ? `$${n.toFixed(0)}`
      : `$${n.toFixed(2)}`;

/**
 * DogMark — single tinted dog silhouette used in the Step-1 pack-size
 * buttons. Reuses the canonical `/brand/poopluckers-dogmark.png` asset
 * (same file as the header logo). Tint is achieved via CSS mask +
 * background-color so the silhouette can re-color cleanly between
 * slate (`--pp-slate`, default) and brand-orange (`--pp-orange`,
 * selected — same token the `.acc` accent uses on the hero headline).
 *
 * We use CSS mask (NOT `filter: hue-rotate`) because the source PNG
 * has multi-color details (orange shovel + brown poop) that
 * hue-rotation would smear into off-brand tones. Mask + bg-color
 * collapses to a clean monochrome silhouette at any color, which is
 * the exact contract requested.
 *
 * `aria-hidden` lives on the parent row so this element stays out of
 * the accessibility tree; the button's text label is the accessible
 * name. The marks are decorative — count is conveyed by the label
 * ("1 dog", "2 dogs", …) so screen-reader users get no duplicate
 * information.
 */
const DOG_MARK_SRC = "/brand/poopluckers-dogmark.png";

function DogMark({ active }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: "22px",
        height: "22px",
        backgroundColor: active ? "var(--pp-orange)" : "var(--pp-slate)",
        WebkitMaskImage: `url(${DOG_MARK_SRC})`,
        maskImage: `url(${DOG_MARK_SRC})`,
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        transition: "background-color 150ms ease",
      }}
    />
  );
}

export default function QuoteTool({ testId = "pricing-quote-tool", onInputsChange = null }) {
  const [dogCount, setDogCount] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const lastRequestId = useRef(0);
  const resultRef = useRef(null);

  // Phase H.1 — surface current inputs to the parent so downstream
  // consumers (e.g. the SavingsPreviewChip on the same /pricing page)
  // can react to changes. Fired on every input flip; callers debounce
  // their own downstream fetches.
  useEffect(() => {
    if (!onInputsChange) return;
    onInputsChange({
      dog_count: dogCount,
      frequency: frequency,
    });
  }, [dogCount, frequency, onInputsChange]);

  // Fire the quote whenever both inputs are populated. Stale-result
  // guard via `lastRequestId` so out-of-order responses can't overwrite
  // a newer answer.
  const fetchQuote = useCallback(async (dc, fq) => {
    if (!dc || !fq) return;
    const reqId = ++lastRequestId.current;
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.post("/quote", {
        dog_count: dc,
        frequency: fq,
      });
      if (reqId !== lastRequestId.current) return;
      setQuote(data);
    } catch {
      if (reqId !== lastRequestId.current) return;
      setError(true);
    } finally {
      if (reqId === lastRequestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dogCount && frequency) fetchQuote(dogCount, frequency);
  }, [dogCount, frequency, fetchQuote]);

  // Scroll the result panel into view when a fresh quote lands.
  useEffect(() => {
    if (quote && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [quote]);

  const handleReset = () => {
    setDogCount(null);
    setFrequency(null);
    setQuote(null);
    setError(false);
  };

  const handleRetry = () => {
    if (dogCount && frequency) fetchQuote(dogCount, frequency);
  };

  return (
    <div
      className="rounded-[24px] border border-pp-line bg-pp-v3-bg p-7 md:p-10"
      style={{ boxShadow: "var(--pp-shadow)" }}
      data-testid={testId}
    >
      {/* STEP 1 — Dogs */}
      <div data-testid={`${testId}-step-dogs`}>
        <div className="flex items-center gap-3">
          <span
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[13px] font-bold"
            style={{
              background: dogCount
                ? "var(--pp-orange)"
                : "rgba(242,151,43,0.15)",
              color: dogCount ? "white" : "var(--pp-orange)",
            }}
            aria-hidden="true"
          >
            1
          </span>
          <p className="text-[13px] font-bold uppercase tracking-[0.08em] text-pp-text-soft">
            How big is your pack?
          </p>
        </div>
        <div
          className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4"
          data-testid={`${testId}-dog-options`}
        >
          {DOG_OPTIONS.map((opt) => {
            const selected = dogCount === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDogCount(opt.value)}
                className="rounded-[16px] border-2 px-4 py-5 text-center transition-all hover:scale-[1.02]"
                style={{
                  borderColor: selected ? "var(--pp-orange)" : "var(--pp-line)",
                  background: selected ? "rgba(242,151,43,0.08)" : "transparent",
                }}
                data-testid={`${testId}-dog-${opt.value}`}
                aria-pressed={selected}
              >
                <span
                  className="mb-2 flex items-center justify-center gap-1"
                  aria-hidden="true"
                  data-testid={`${testId}-dog-${opt.value}-marks`}
                >
                  {Array.from({ length: opt.value }).map((_, i) => (
                    <DogMark key={i} active={selected} />
                  ))}
                </span>
                <span className="block text-[20px] font-extrabold text-pp-slate">
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* STEP 2 — Frequency. Disabled until step 1 is set. */}
      <div className="mt-10" data-testid={`${testId}-step-frequency`}>
        <div className="flex items-center gap-3">
          <span
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[13px] font-bold"
            style={{
              background:
                frequency
                  ? "var(--pp-orange)"
                  : dogCount
                    ? "rgba(242,151,43,0.15)"
                    : "var(--pp-line)",
              color: frequency
                ? "white"
                : dogCount
                  ? "var(--pp-orange)"
                  : "var(--pp-text-soft)",
            }}
            aria-hidden="true"
          >
            2
          </span>
          <p
            className="text-[13px] font-bold uppercase tracking-[0.08em]"
            style={{
              color: dogCount ? "var(--pp-text-soft)" : "var(--pp-text-muted)",
            }}
          >
            How often?
          </p>
        </div>
        <div
          className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3"
          data-testid={`${testId}-frequency-options`}
        >
          {FREQUENCY_OPTIONS.map((opt) => {
            const selected = frequency === opt.value;
            const disabled = !dogCount;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => setFrequency(opt.value)}
                className="rounded-[16px] border-2 px-5 py-5 text-left transition-all enabled:hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-40"
                style={{
                  borderColor: selected ? "var(--pp-orange)" : "var(--pp-line)",
                  background: selected ? "rgba(242,151,43,0.08)" : "transparent",
                }}
                data-testid={`${testId}-frequency-${opt.value}`}
                aria-pressed={selected}
              >
                <span className="block text-[17px] font-bold text-pp-slate">
                  {opt.label}
                </span>
                <span className="mt-1 block text-[13px] text-pp-text-soft leading-relaxed">
                  {opt.sub}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* RESULT PANEL */}
      <div ref={resultRef} className="mt-10" data-testid={`${testId}-result`}>
        {!dogCount || !frequency ? (
          <p
            className="text-[15px] text-pp-text-soft italic"
            data-testid={`${testId}-empty`}
          >
            Pick a pack size and how often to see your live per-visit price.
          </p>
        ) : loading ? (
          <div
            className="flex items-center gap-3 text-pp-text-soft"
            data-testid={`${testId}-loading`}
          >
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            <span className="text-[15px]">Calculating your quote…</span>
          </div>
        ) : error ? (
          <div
            className="rounded-[16px] border border-pp-line bg-pp-v3-surface p-6"
            data-testid={`${testId}-error`}
          >
            <p className="text-[15px] font-bold text-pp-slate">
              We couldn’t fetch your quote.
            </p>
            <p className="mt-1 text-[14px] text-pp-text-soft">
              Try again — usually a flaky connection.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              className="pp-btn pp-btn-primary mt-4"
              data-testid={`${testId}-error-retry`}
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Retry
            </button>
          </div>
        ) : quote ? (
          <div
            className="rounded-[20px] border border-pp-line bg-pp-v3-surface p-7 md:p-8"
            data-testid={`${testId}-quote`}
          >
            <div className="flex flex-col gap-1">
              <p className="text-[13px] font-bold uppercase tracking-[0.08em] text-pp-text-soft">
                {DOG_OPTIONS.find((o) => o.value === dogCount)?.capped
                  ? "Starts at"
                  : "Your per-visit price"}
              </p>
              <p
                className="font-heading text-[44px] font-extrabold leading-none text-pp-slate md:text-[56px]"
                data-testid={`${testId}-quote-total`}
              >
                {fmtMoney(quote.total)}
              </p>
              <p
                className="text-[14px] text-pp-text-soft"
                data-testid={`${testId}-quote-detail`}
              >
                {DOG_OPTIONS.find((o) => o.value === dogCount)?.label.toLowerCase()} ·{" "}
                {FREQUENCY_OPTIONS.find((o) => o.value === frequency)?.label.toLowerCase()}
                {quote.recurring_discount_pct > 0 && (
                  <>
                    {" "}· {Math.round(quote.recurring_discount_pct)}% off recurring visits
                  </>
                )}
              </p>
            </div>

            {/* Phase C — First-Clean Fee promotional row. Marketing
                traffic is always is_first_booking=true (backend
                default), so this row renders whenever the config has
                a positive fee anchor. Three visual states drive off
                the SAME response fields the wizard/BookingSummary
                use — no local math, no hardcoded literals. Family E
                integrity guard pins the config→FE linkage. */}
            {quote.is_first_booking && (quote.first_clean_fee_cents || 0) > 0 && (
              <div
                className="mt-5 flex flex-wrap items-center gap-3 rounded-[14px] border border-pp-line bg-pp-v3-bg px-4 py-3"
                data-testid={`${testId}-first-clean-fee`}
              >
                <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-pp-text-soft">
                  First-clean fee
                </span>
                {quote.first_clean_fee_waived ? (
                  <>
                    <span
                      className="text-[15px] font-bold text-pp-text-soft line-through decoration-2"
                      data-testid={`${testId}-first-clean-fee-anchor`}
                    >
                      {fmtMoney((quote.first_clean_fee_cents || 0) / 100)}
                    </span>
                    <span
                      className="inline-flex items-center rounded-full bg-brand-orange/15 px-2.5 py-0.5 text-[0.7rem] font-bold uppercase tracking-wider text-brand-orange"
                      data-testid={`${testId}-first-clean-fee-tag`}
                    >
                      Waived — launch special
                    </span>
                  </>
                ) : (
                  <span
                    className="text-[15px] font-bold text-pp-slate"
                    data-testid={`${testId}-first-clean-fee-charge`}
                  >
                    {fmtMoney((quote.first_clean_fee_charge_cents || 0) / 100)}
                  </span>
                )}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {/* Pre-Launch Demand Capture (2026-07-21) — customer
                  CTA swept to waitlist. QuoteTool still shows the
                  numeric quote (transparent pricing preview) but
                  the acquisition path is the waitlist, not signup. */}
              <Link
                to="/#waitlist"
                className="pp-btn pp-btn-primary"
                data-testid={`${testId}-cta-book`}
              >
                Join the waitlist
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <button
                type="button"
                onClick={handleReset}
                className="pp-btn pp-btn-ghost"
                data-testid={`${testId}-cta-reset`}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Start over
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
